const SENSITIVE_KEYS = new Set([
  'password',
  'passwd',
  'pwd',
  'creditcard',
  'credit_card',
  'cc_number',
  'cardnumber',
  'cvv',
  'cvc',
  'ssn',
  'socialsecurity',
  'paymenttoken',
  'stripetoken',
  'apikey',
  'api_key',
  'secret',
  'authtoken',
  'bearertoken',
  'token',
  'access_token',
  'accesstoken',
  'refresh_token',
  'refreshtoken',
  'id_token',
  'idtoken',
]);

const REDACTED = '[REDACTED]';

// Value-pattern scrubbing for high-confidence sensitive tokens that travel through
// arbitrary keys (e.g. errorMessage strings, opaque metadata). Conservative on
// purpose: only patterns with near-zero false-positive rate. `userEmail` is
// intentionally NOT scrubbed — it is the primary user identifier in the schema.
const VALUE_PATTERNS: { pattern: RegExp; label: string }[] = [
  { pattern: /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, label: 'jwt' },
  { pattern: /\b(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]{16,}\b/g, label: 'api-key' },
  { pattern: /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, label: 'bearer' },
];

// Card numbers need a Luhn check because a length-only regex (13-19 digits)
// produces false positives on order IDs, phone numbers, and Athena row IDs.
// Real PANs always pass Luhn; arbitrary integers virtually never do, so a
// match + Luhn-pass is a near-zero-false-positive signal.
const CARD_CANDIDATE = /\b\d{13,19}\b/g;
const passesLuhn = (digits: string): boolean => {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.charCodeAt(i) - 48;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

const isSensitiveKey = (key: string): boolean => SENSITIVE_KEYS.has(key.toLowerCase());

const scrubString = (s: string): string => {
  let out = s;
  for (const { pattern, label } of VALUE_PATTERNS) {
    out = out.replace(pattern, `[REDACTED:${label}]`);
  }
  out = out.replace(CARD_CANDIDATE, (match) =>
    passesLuhn(match) ? '[REDACTED:card]' : match,
  );
  return out;
};

export const scrubPii = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return scrubString(value);
  if (Array.isArray(value)) return value.map(scrubPii);
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = isSensitiveKey(k) ? REDACTED : scrubPii(v);
    }
    return out;
  }
  return value;
};
